import { swNumberFilter } from "app/filters/ngFilters";
import { formatNumberWithCommas } from "app/pages/sales-intelligence/utils/numbers";

import type { ContactEntryPhone, ContactSearchEntry, RevealedContactSearchEntry } from "../types/common";
import { ContactEntryType } from "../types/common";
import type { ContactIdCompanyPair } from "../types/dto";

/**
 * Formats given number using `swNumberFilter()`
 * @param n
 * @example ```
 *     formatNumberOfResults(0) -> "0"
 *     formatNumberOfResults(12435) -> "12,435"
 * ```
 */
function formatLargeNumber(n: number): string {
    if (typeof n === "undefined") {
        return "";
    }

    if (n === 0) {
        return String(n);
    }

    return swNumberFilter()(n);
}

/**
 * Returns correct text for pagination caption
 * @param properties
 */
export function getPaginationCaptionText(properties: { page: number; itemsCount: number; itemsPerPage: number }) {
    const { page, itemsCount, itemsPerPage } = properties;

    return `${page} out of ${formatNumberWithCommas(Math.ceil(itemsCount / itemsPerPage))}`;
}

/**
 * Fixes missing protocol in the LinkedIn url
 * @param contactEntry
 */
export function fixLinkedInUrl<E extends { linkedin: string }>(contactEntry: E): E {
    if (contactEntry.linkedin?.length > 0 && !contactEntry.linkedin.startsWith("http")) {
        return {
            ...contactEntry,
            linkedin: `https://${contactEntry.linkedin}`,
        };
    }

    return contactEntry;
}

/**
 * Type helper to check whether given contact entry has revelad details inside
 * @param contactEntry
 */
export function hasDetailedInfo(
    contactEntry: ContactSearchEntry | RevealedContactSearchEntry,
): contactEntry is RevealedContactSearchEntry {
    return typeof (contactEntry as RevealedContactSearchEntry).revealedInfo !== "undefined";
}

/**
 * Split phone number objects into 2 lists (mobile/direct)
 * @param phoneNumbers
 */
export function getMobileAndDirectPhones(phoneNumbers: ContactEntryPhone[] | undefined) {
    const groupOfPhones: {
        mobilePhones: ContactEntryPhone[];
        directPhones: ContactEntryPhone[];
    } = {
        mobilePhones: [],
        directPhones: [],
    };

    if (!Array.isArray(phoneNumbers)) {
        return groupOfPhones;
    }

    return phoneNumbers.reduce((group, phone) => {
        switch (phone.internalType) {
            case ContactEntryType.Direct:
                group.directPhones.push(phone);
                break;
            case ContactEntryType.Mobile:
                group.mobilePhones.push(phone);
                break;
            default:
                break;
        }

        return group;
    }, groupOfPhones);
}

/**
 * Appends a phone number type if exists
 * @param phoneNumber
 * @param type
 */
export function appendPhoneNumberTypeIfExists(phoneNumber: string, type?: ContactEntryPhone["type"]) {
    if (typeof type !== "undefined") {
        return `${phoneNumber} (${type})`;
    }

    return phoneNumber;
}

export const isRevealable = (
    contact: Pick<ContactSearchEntry, "hasDirectPhoneNumbers" | "hasMobilePhoneNumbers" | "hasValidEmails">,
): boolean => {
    return contact.hasDirectPhoneNumbers || contact.hasMobilePhoneNumbers || contact.hasValidEmails;
};

const booleanToNumber = (boolValue?: boolean) => (boolValue ? 1 : 0);
export const numberToBoolean = (number: string) => (number === "0" ? false : true);

/**
 * Retrieves a unique ID for given contact entry by combining `id`, `site` and `title` fields
 * @param entry
 */
export function toUniqueContactId<
    E extends {
        id: string;
        site: string;
        originalSite: string;
        companyId: string;
        title: string;
        hasValidEmails?: boolean;
        hasValidEmailsSuppressed?: boolean;
        hasDirectPhoneNumbers?: boolean;
        hasDirectPhoneNumbersSuppressed?: boolean;
        hasMobilePhoneNumbers?: boolean;
        hasMobilePhoneNumbersSuppressed?: boolean;
    },
>({
    id,
    site,
    originalSite,
    title,
    companyId,
    hasValidEmails,
    hasValidEmailsSuppressed,
    hasDirectPhoneNumbers,
    hasDirectPhoneNumbersSuppressed,
    hasMobilePhoneNumbers,
    hasMobilePhoneNumbersSuppressed,
}: E) {
    return `${id}:${site}:${title}:${companyId}:${booleanToNumber(hasValidEmails)}:${booleanToNumber(
        hasDirectPhoneNumbers,
    )}:${booleanToNumber(hasMobilePhoneNumbers)}:${booleanToNumber(hasValidEmailsSuppressed)}:${booleanToNumber(
        hasMobilePhoneNumbersSuppressed,
    )}:${booleanToNumber(hasDirectPhoneNumbersSuppressed)}:${originalSite}`;
}

/**
 * Creates the `ContactIdCompanyPair` from the given unique id
 * @param id
 */
export function fromUniqueIdToPair(id: string): ContactIdCompanyPair {
    const [contactId, prospect] = id.split(":");

    return {
        contactId,
        prospect,
    };
}

export function getContactIdFromUniqueId(id: string): string {
    const colonIndex = id.indexOf(":");
    if (colonIndex !== -1) {
        return id.slice(0, colonIndex);
    }
    return id;
}
